import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAGE_CONFIGS } from '__queries/query-keys';
import TeamMemberService from '__services/TeamMemberService';

type UpdateConfigsProp = {
  activityObjectId: string | null;
  defaultColumnSettings: { columns: any[]; sort: any };
  columnsConfigSetFunc: (
    activityObjectId: string | null,
    config: any
  ) => Promise<void>;
  pageConfigSetFunc: (
    activityObjectId: string | null,
    config: any
  ) => Promise<void>;
  columnsConfigGetFunc: (activityObjectId: string | null) => Promise<any>;
  pageConfigGetFunc: (activityObjectId: string | null) => Promise<any>;
  configPropKey: 'scheduled_config' | 'responses_config';
  columnsConfigPropKey: 'scheduled_column_config' | 'responses_column_config';
};

export const useConfig = ({
  activityObjectId,
  defaultColumnSettings,
  configPropKey,
  columnsConfigPropKey,
}: UpdateConfigsProp) => {
  const queryClient = useQueryClient();

  const {
    data: {
      [columnsConfigPropKey]: columnSettings = {},
      [configPropKey]: pageSettings = {},
    } = {},
    isLoading: isPageConfigLoading,
  } = useQuery({
    queryKey: PAGE_CONFIGS.ACTIVITY_TABLE_CONFIG(activityObjectId),
    queryFn: () =>
      TeamMemberService.getActivityTablePageConfig(activityObjectId),
    enabled: !!activityObjectId,
  });

  const updateColumnsConfig = useCallback(
    async (val: { columns: any[]; sort: any }) => {
      const rmLocalizeFromDefaultColumns = val?.columns.map((el) => {
        const defaultItem = defaultColumnSettings.columns.find(
          (e: { id: any }) => e.id === el.id
        );
        if (!defaultItem) return el;
        return { ...el, label: el.label || defaultItem.label };
      });
      await TeamMemberService.setActivityTablePageConfig(activityObjectId, {
        [columnsConfigPropKey]: {
          ...columnSettings,
          ...val,
          columns: rmLocalizeFromDefaultColumns,
        },
      });
      queryClient.invalidateQueries({
        queryKey: PAGE_CONFIGS.ACTIVITY_TABLE_CONFIG(activityObjectId),
      });
    },
    [
      activityObjectId,
      defaultColumnSettings,
      columnSettings,
      queryClient,
      columnsConfigPropKey,
    ]
  );

  const updatePageConfig = useCallback(
    async (val: any) => {
      await TeamMemberService.setActivityTablePageConfig(activityObjectId, {
        [configPropKey]: {
          ...pageSettings,
          ...val,
        },
      });
      queryClient.invalidateQueries({
        queryKey: PAGE_CONFIGS.ACTIVITY_TABLE_CONFIG(activityObjectId),
      });
    },
    [activityObjectId, pageSettings, queryClient, configPropKey]
  );

  return {
    columnSettings,
    pageSettings,
    isPageConfigLoading,
    updateColumnsConfig,
    updatePageConfig,
  };
};
